import { Injectable } from '@angular/core';

import { substr } from 'stringz';

import { DocumentViewOptions } from 'app/modules/collection/components/document/document-view/document-view.component';
import { AbstractFeatureChartReport } from 'app/modules/widget/services/chart-report/chart-report-abstract/chart-report-abstract';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { Utils } from '../utils/utils';
import { Section as AnalysisSection, DocumentAnalysis } from './storage-api/analysis.service';
import { Document, DocumentSmartSummary, SectionKeyMoment } from './storage-api/document.service';
import { FieldService } from './storage-api/field.service';

export type DocumentSearchType = 'default' | 'unredacted' | 'untranslated';

export interface HighlightLocation {
  offset: number;
  length: number;
  sentimentScore: number;
  section?: number;
  sectionCharOffset?: number;
  color?: string;
  highlightClass?: string;
  priority?: number;
  type?: 'user-search';
}

export interface DocumentViewSection {
  sectionId: number;
  internalId?: string;
  value: string;
  offset: number;
  playbackOffset?: number;
  playbackDisplay?: string;
  metadata?: { [key: string]: string };
  initial?: string;
  alignment?: 'left' | 'right';
  segment?: string[];
  label: string;
  title?: string;
  date?: Date;
  type?: 'utterance' | 'summary' | 'other';
  keyMoments?: SectionKeyMoment[];
  sentimentScore?: number;
}

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class NlpOutputService {
  /**
   * Create formatted text for highlighting mentions in document.
   * Used for load document description (innerHTML) content when user clicks on analytic item.
   */
  public createDocumentSections(
    document: Document,
    features: any[],
    withPhrases: boolean,
    options: DocumentViewOptions,
    searchType: DocumentSearchType,
    selectionClass?: string
  ): DocumentViewSection[] {
    if (!document) {
      return [];
    }

    const highlightClass = selectionClass || 'highlight';
    const sections: DocumentViewSection[] = this.buildSections(document, searchType);

    if (features.length) {
      const getColor = (location: HighlightLocation) => {
        if (location.color) {
          return location.color;
        }
        if (withPhrases) {
          return AbstractFeatureChartReport.getSentimentColor(
            options.sentimentRanges.feature,
            location.sentimentScore
          );
        }

        return '#7CB5EC';
      };

      const highlight = (location: HighlightLocation, color: string) => {
        const section = sections.find((s) => s.sectionId === location.section) ?? sections[0];

        const start = document.sectioned
          ? location.sectionCharOffset
          : location.offset - section.offset;
        const len = location.length;
        const highlightClassOverride = location.highlightClass || highlightClass;

        section.value =
          substr(section.value, 0, start) +
          Utils.fauxEscapeHtml(
            `<em class="${highlightClassOverride} highlight-${color.substring(1)}">`
          ) +
          substr(section.value, start, len) +
          Utils.fauxEscapeHtml('</em>') +
          substr(section.value, start + len);
      };

      let locations: HighlightLocation[] = [];

      // FIRST PASS:
      // highlight mentions with offsets
      for (const feature of features) {
        // collect all locations for every mention
        if (feature.mentions) {
          for (const mention of feature.mentions) {
            for (const location of mention.locations) {
              const offset = location.char_offset ?? location.charOffset ?? location.offset;
              const length = location.char_length ?? location.charLength ?? location.length;
              const loc = {
                offset,
                length,
                sentimentScore: location.sentimentScore,
                section: location.section,
                sectionCharOffset: location.section_char_offset,
              };

              if (withPhrases && !feature.themes) {
                loc.sentimentScore = mention.sentiment_score ?? feature.sentimentScore;
              }

              locations.push(loc);
            }
          }
        }

        if (withPhrases && feature.phrases) {
          for (const phrase of feature.phrases) {
            const offset = phrase.phrase.char_offset ?? phrase.phrase.charOffset ?? phrase.offset;
            const length = phrase.phrase.char_length ?? phrase.phrase.charLength ?? phrase.length;
            locations.push({
              offset,
              length,
              sentimentScore: phrase.sentiment_score,
              section: phrase.phrase.section,
              sectionCharOffset: phrase.phrase.section_char_offset,
            });
          }
        }
      }

      // Sort collected locations by offset and length
      // Greater offsets come first to maintain offset counter consistency during replace
      locations = this.sortLocations(locations);

      const mergedLocations: HighlightLocation[] = this.mergeOverlappingLocations(locations);

      // Highlight locations
      for (const location of mergedLocations) {
        highlight(location, getColor(location));
      }

      // Convert search features to locations
      const searchFeatures = [];
      features.forEach((feature) => {
        if (feature.type !== 'user-search') {
          return;
        }

        const mentionRegexp = new RegExp(
          `(${feature.title?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
          'ig'
        );

        let match: RegExpExecArray | null;
        sections.forEach((s) => {
          while ((match = mentionRegexp.exec(s.value)) !== null) {
            searchFeatures.push({
              ...feature,
              offset: document.sectioned ? s.offset + match.index! : match.index!,
              length: match[0].length,
              sectionCharOffset: match.index!,
              section: document.sectioned ? s.sectionId : undefined,
            });
          }
        });
      });

      // Sort features
      const sortedFeatures = this.sortLocations([
        ...features.filter((f) => f.type !== 'user-search'),
        ...searchFeatures,
      ]);

      const mergedFeatures = this.mergeOverlappingLocations(sortedFeatures) as any[];

      // SECOND PASS:
      // highlight single occurences
      for (const feature of mergedFeatures) {
        const color = getColor(feature);

        if (!feature.mentions && feature.title) {
          if (feature.offset >= 0 && feature.length > 0) {
            highlight(feature, color);
          }
        }
      }
    }

    sections.forEach((s) => {
      // escape actual HTML from the document
      s.value = Utils.escapeHtml(s.value);

      // unescape our faux escaped hightlight html
      s.value = Utils.fauxUnescapeHtml(s.value);
    });

    return sections;
  }

  private mergeOverlappingLocations(locations: HighlightLocation[]) {
    const mergedLocations: HighlightLocation[] = [];
    // Merge possibly overlapping locations
    for (const location of locations) {
      const startA = location.offset;
      const endA = location.offset + location.length;

      const contained = mergedLocations.filter((l) => {
        if (!Utils.areEqualOrFalsy(location.section, l.section)) {
          return false;
        }

        const startB = l.offset;
        const endB = l.offset + l.length;

        return startA <= endB && startB <= endA;
      });

      if (contained.length > 0) {
        for (const containedLocation of contained) {
          // If priorities differ, keep both, because these locations are handled during normalization.
          if (location.priority !== containedLocation.priority) {
            mergedLocations.push({ ...location });
            continue;
          }
          if (startA < containedLocation.offset) {
            containedLocation.offset = startA;
            containedLocation.sectionCharOffset = location.sectionCharOffset;
          }
          if (endA > containedLocation.offset + containedLocation.length) {
            containedLocation.length = location.length;
          }
        }
      } else {
        mergedLocations.push({ ...location });
      }
    }

    // Return normalized locations
    return this.normalizeLocations(mergedLocations);
  }

  private normalizeLocations(locations: HighlightLocation[]): HighlightLocation[] {
    // Work on a copy
    let result: HighlightLocation[] = [...locations];

    const isOverlapping = (a: HighlightLocation, b: HighlightLocation): boolean => {
      const endFn = (i: HighlightLocation) => i.offset + i.length;
      return (
        a.offset < endFn(b) && b.offset < endFn(a) && Utils.areEqualOrFalsy(a.section, b.section)
      );
    };

    // Keep normalizing until no overlaps exist
    let changed = true;

    while (changed) {
      changed = false;

      // Sort for determinism
      result = this.sortLocations(result);

      outer: for (let i = 0; i < result.length; i++) {
        for (let j = i + 1; j < result.length; j++) {
          const a = result[i];
          const b = result[j];

          if (!isOverlapping(a, b) || a.priority === b.priority) {
            continue;
          }

          // Remove the overlapping pair
          result.splice(j, 1);
          result.splice(i, 1);

          // Split and reinsert
          const split = this.splitOverlappingLocations(a, b);
          result.push(...split);

          changed = true;
          // restart from scratch
          break outer;
        }
      }
    }

    // Final sort
    result = this.sortLocations(result);

    // Remove same locations
    return this.removeSameLocations(this.sortLocations(result));
  }

  private sortLocations(locations: HighlightLocation[]) {
    return [...locations].sort((a, b) => {
      const ret = b.offset - a.offset;
      return ret || b.length - a.length;
    });
  }

  private removeSameLocations(locations: HighlightLocation[]) {
    return locations.reduce((uniqueArray, location) => {
      return uniqueArray.find((uniqueLocation) => {
        return (
          location.offset === uniqueLocation.offset &&
          location.length === uniqueLocation.length &&
          location.section === uniqueLocation.section
        );
      })
        ? uniqueArray
        : [...uniqueArray, location];
    }, []);
  }

  private splitOverlappingLocations(
    left: HighlightLocation,
    right: HighlightLocation
  ): HighlightLocation[] {
    const endFn = (i: HighlightLocation) => i.offset + i.length;
    const leftEnd = endFn(left);
    const rightEnd = endFn(right);

    const overlapStart = Math.max(left.offset, right.offset);
    const overlapEnd = Math.min(leftEnd, rightEnd);

    // No overlap → return both unchanged
    if (overlapStart >= overlapEnd) {
      return [left, right];
    }

    const higher = (left.priority ?? 0) >= (right.priority ?? 0) ? left : right;
    const lower = higher === left ? right : left;

    const result: HighlightLocation[] = [];

    // Higher-priority interval is kept whole
    result.push({
      ...higher,
    });

    // Left non-overlapping part of lower
    if (lower.offset < overlapStart) {
      result.push({
        ...lower,
        length: overlapStart - lower.offset,
      });
    }

    // Right non-overlapping part of lower
    if (endFn(lower) > overlapEnd) {
      result.push({
        ...lower,
        offset: overlapEnd,
        length: endFn(lower) - overlapEnd,
        sectionCharOffset:
          higher.sectionCharOffset !== undefined
            ? higher.sectionCharOffset + higher.length
            : undefined,
      });
    }

    return result;
  }

  public createSmartSummarySections(document: DocumentSmartSummary): DocumentViewSection[] {
    return document.results.map((res, index) => {
      const section: DocumentViewSection = {
        sectionId: index,
        value: res.text,
        offset: index > 0 ? document.results[index - 1].text.length : 0,
        label: '',
      };

      return section;
    });
  }

  private buildSections(document: Document, searchType: DocumentSearchType): DocumentViewSection[] {
    const analysis: DocumentAnalysis =
      document.analyses?.length && document.analyses[document.analyses.length - 1];
    if (!analysis) {
      return [];
    }
    let analysisSections: AnalysisSection[] = [];
    let playbackOffsetFieldName = 'unset';
    if (document.sectioned && analysis.flatSections) {
      const allFields = analysis.flatSections.flatMap((section) =>
        Object.keys(section.metadata || {})
      );
      const uniqueFields = new Set(allFields);
      const findByName = (wantName) => {
        let foundFieldName = 'unset';
        const foundFieldList = Array.from(uniqueFields).filter(
          (fieldName) =>
            fieldName === wantName || FieldService.getFieldLongName(fieldName) === wantName
        );
        if (foundFieldList) {
          foundFieldName = foundFieldList[0];
        }
        return foundFieldName;
      };
      playbackOffsetFieldName = findByName('playback_offset');
      analysisSections = analysis.flatSections.filter((section) => section.value !== undefined);
    }

    // if there are no sections in document the text of the document will be used as one section
    if (!analysisSections.length) {
      const section = {
        redactedValue: analysis.redactedText ?? analysis.translatedText,
        value: document.text,
        sectionId: null,
        charOffset: 0,
      } as AnalysisSection;
      if (analysis.translatedText) {
        section.value = analysis.translatedText;
        section.originalValue = document.text;
      }
      analysisSections = [section];
    }

    return analysisSections.map((s) => {
      let value = s.value;
      switch (searchType) {
        case 'default':
          value = s.redactedValue ?? s.value;
          break;
        case 'unredacted':
          value = s.value;
          break;
        case 'untranslated':
          value = s.originalValue;
          break;
      }

      const section: DocumentViewSection = {
        sectionId: s.sectionId,
        internalId: s.sectionPropertiesId,
        value,
        offset: s.charOffset,
        label: s.speakerName,
        title: s.sectionLabel,
        segment: s.segment,
        type: s.sectionType,
        keyMoments: s.keyMoments,
        sentimentScore: s.sentimentScore,
      };

      if (s.sectionTime) {
        section.date = new Date(s.sectionTime);
      }

      if (s.metadata) {
        section.metadata = s.metadata;

        if (s.metadata[playbackOffsetFieldName] || s.metadata[playbackOffsetFieldName] === 0) {
          section.playbackOffset = s.metadata[playbackOffsetFieldName];
          if (s.metadata[playbackOffsetFieldName] === 0) {
            section.playbackOffset = 0.0;
          }
          section.playbackDisplay = Utils.toHoursMinutesSeconds(section.playbackOffset);
        }
      }

      return section;
    });
  }
}
